// libraries.
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// selectors.
import {
  getCountry,
  getEmail,
} from '@domains/user/store/slices/user-slice/user-slice';
import {
  getSelectedCoordinates,
  getSelectedNid,
  getSelectedBusinessLine,
} from '../store/slices/georeferencing-slice';

// hooks.
import { useGetLeadDetailInfoQuery } from '@domains/lead-detail';
import { useSaveHermesMutation } from '@domains/hermes/services/hermes.endpoints';

// utils.
import { processWebSocketMessage } from '../utils';

// types.
import { GeoreferencingOverrides, UseGeoreferencingWebSocketProps } from '../interfaces/georeferencing.types';
import { LeadDetail } from '@domains/lead-detail/interfaces/interfaces';

const WS_READY_STATE: Record<number, string> = {
  0: 'CONNECTING',
  1: 'OPEN',
  2: 'CLOSING',
  3: 'CLOSED',
};

const wsLog = (msg: string, ...args: any[]) =>
  console.log(`[Georef WS] ${msg}`, ...args);

const wsWarn = (msg: string, ...args: any[]) =>
  console.warn(`[Georef WS] ${msg}`, ...args);

const wsError = (msg: string, ...args: any[]) =>
  console.error(`[Georef WS] ${msg}`, ...args);

const selectWebSocketAction = (country?: string) => {
  switch (country) {
    case 'CO':
      return 'put_lead_co';
    case 'MX':
      return 'put_lead_mx';
    default:
      return 'put_lead_co';
  }
};

const handleError = (errorMsg: string, onError?: (error: string) => void) => {
  console.error(errorMsg);
  toast.error(errorMsg);
  if (onError) {
    onError(errorMsg);
  }
};

const buildPayload = (
  country: string | undefined,
  email: string,
  coordinates: google.maps.LatLngLiteral,
  dealId: number,
  leadDetail: LeadDetail,
  overrides?: GeoreferencingOverrides
) => {
  const propertyTypeId =
    overrides?.propertyTypeId ?? (Number(leadDetail.property?.property_type_id) || undefined);
  const address = overrides?.address ?? leadDetail.location?.address;

  if (country === 'MX') {
    // HermesMessageMX: campos en inglés
    return {
      deal_id: dealId,
      hubspot_ignore: ['dealname'],
      country: 'MX',
      agent: email,
      latitude: coordinates.lat,
      longitude: coordinates.lng,
      ...(propertyTypeId && { property_type_id: propertyTypeId }),
      ...(address && { address }),
      ...(overrides?.area !== undefined && { area: overrides.area }),
      ...(overrides?.yearsOld !== undefined && { years_old: overrides.yearsOld }),
    };
  }

  // HermesMessageCO: campos en español
  const cityId = leadDetail.location?.city?.id;
  return {
    deal_id: dealId,
    hubspot_ignore: ['dealname'],
    country: 'CO',
    agente: email,
    latitud: coordinates.lat,
    longitud: coordinates.lng,
    ...(cityId && { ciudad_id: cityId }),
    ...(propertyTypeId && { tipo_inmueble_id: propertyTypeId }),
    ...(address && { direccion: address }),
    ...(overrides?.area !== undefined && { area: overrides.area }),
    ...(overrides?.yearsOld !== undefined && { anos_antiguedad: overrides.yearsOld }),
    ...(overrides?.buildingComplex !== undefined && { conjunto_edificio: overrides.buildingComplex }),
    ...(overrides?.rooms !== undefined && { num_habitaciones: overrides.rooms }),
    ...(overrides?.elevators !== undefined && { num_ascensores: overrides.elevators }),
  };
};

// El backend recalcula lote_id, anomalías, CORE y Hubspot de forma asíncrona
// después de change_coordinates. Mantenemos el loading activo y esperamos antes
// de mostrar el éxito, para que el usuario no vea datos incompletos.
const REFETCH_DELAY_MS = 4000;

const handleGeoreferencingSuccess = async (
  setIsLoading: (loading: boolean) => void,
  refetchLeadDetail: () => void,
  onSuccess?: () => void
) => {
  wsLog(`⏳ Esperando ${REFETCH_DELAY_MS / 1000}s para que el backend termine de procesar (lote_id, anomalías, CORE...)...`);

  await new Promise(resolve => setTimeout(resolve, REFETCH_DELAY_MS));

  wsLog('🔄 Refetch leadDetail ejecutado (post-delay)');
  const refetchResult = await (refetchLeadDetail as () => Promise<any>)();
  wsLog('📥 Resultado del refetch:', {
    property_type_id: refetchResult?.data?.property?.property_type_id,
    property_type: refetchResult?.data?.property?.property_type,
    address: refetchResult?.data?.location?.address,
    coordinates: refetchResult?.data?.location?.coordinates,
  });

  setIsLoading(false);
  toast.dismiss('loadingGeoreferencing');
  toast.success('Información actualizada correctamente', {
    toastId: 'successGeoreferencing',
  });
  if (onSuccess) {
    onSuccess();
  }
};

const handleGeoreferencingError = (
  setIsLoading: (loading: boolean) => void,
  errorMessage: string,
  onError?: (error: string) => void
) => {
  setIsLoading(false);
  toast.dismiss('loadingGeoreferencing');
  toast.error('Error al actualizar la información', {
    toastId: 'errorGeoreferencing',
  });
  if (onError) {
    onError(errorMessage);
  }
};

export const useGeoreferencingWebSocket = ({
  onSuccess,
  onError,
  overrides,
}: UseGeoreferencingWebSocketProps) => {
  const dispatch = useDispatch();
  const country = useSelector(getCountry);
  const email = useSelector(getEmail);
  const coordinates = useSelector(getSelectedCoordinates);
  const nid = useSelector(getSelectedNid);
  const selectedBusinessLine = useSelector(getSelectedBusinessLine);

  const [ws, setWs] = useState<WebSocket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const websocketResponsesRef = useRef<any[]>([]);
  const pendingRequestRef = useRef(false);
  // Ref para overrides: garantiza que onmessage siempre use el valor más reciente
  // aunque overrides cambie en cada render (no está en el dep array del useEffect)
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;

  const { data: leadDetail, refetch: refetchLeadDetail } =
    useGetLeadDetailInfoQuery({ country, nid: nid || '' }, { skip: !nid });

  const [saveHermes] = useSaveHermesMutation();

  // Conectar WebSocket
  useEffect(() => {
    wsLog('Creando conexión WebSocket...');
    const websocket = new WebSocket(
      process.env.ENV_WEBSOCKET_BACKBONE as string
    );
    setWs(websocket);

    return () => {
      wsLog('Cerrando WebSocket por desmontaje del componente');
      websocket.close();
    };
  }, []);

  // Configurar eventos del WebSocket
  useEffect(() => {
    if (!ws) return;

    ws.onopen = () => {
      wsLog('✅ Conexión establecida (readyState OPEN)');
    };

    ws.onmessage = async event => {
      wsLog('📨 Mensaje recibido del backbone', {
        pendingRequest: pendingRequestRef.current,
        data: event.data,
      });

      if (!pendingRequestRef.current) {
        wsWarn('🚫 Mensaje ignorado: no hay petición pendiente de Georeferenciación (posible broadcast)');
        return;
      }
      pendingRequestRef.current = false;
      wsLog('✔️ Mensaje aceptado, procesando...');

      try {
        const parsedData = JSON.parse(event.data);

        if (!parsedData?.body) {
          wsWarn('Sin "body" en el mensaje recibido:', parsedData);
          return;
        }

        const parsedBody = JSON.parse(parsedData.body);
        wsLog('Body parseado:', parsedBody);

        await processWebSocketMessage({
          parsedBody,
          selectedBusinessLine,
          loadingHandlers: {
            setIsLoading,
            refetchLeadDetail,
            onSuccess,
            onError,
          },
          callbacks: {
            handleGeoreferencingSuccess,
            handleGeoreferencingError,
          },
          leadData: {
            leadDetail,
            coordinates,
            email,
            country,
            overrides: overridesRef.current,
          },
          saveHermes,
        });

        websocketResponsesRef.current.push(parsedBody);
      } catch (error: any) {
        wsError('Error procesando respuesta del WebSocket:', error);
        handleGeoreferencingError(
          setIsLoading,
          'Error al procesar la respuesta del servidor',
          onError
        );
      }
    };

    ws.onerror = error => {
      wsError('❌ Error en la conexión WebSocket:', error);
      pendingRequestRef.current = false;
      handleGeoreferencingError(
        setIsLoading,
        'Error de conexión con el servidor',
        onError
      );
    };

    ws.onclose = (event?: CloseEvent) => {
      wsWarn(`🔌 WebSocket cerrado — código: ${event?.code ?? 'N/A'}, razón: "${event?.reason || 'sin razón'}", limpio: ${event?.wasClean ?? 'N/A'}`);
      wsLog('Reconectando...');
      pendingRequestRef.current = false;
      setWs(new WebSocket(process.env.ENV_WEBSOCKET_BACKBONE as string));
      setIsLoading(false);
    };
  }, [
    ws,
    dispatch,
    onSuccess,
    onError,
    refetchLeadDetail,
    selectedBusinessLine,
    saveHermes,
    leadDetail,
    coordinates,
    email,
    country,
  ]);

  const isWsReady = ws?.readyState === WebSocket.OPEN;

  const sendGeoreferencing = () => {
    const readyStateLabel = ws ? WS_READY_STATE[ws.readyState] ?? 'UNKNOWN' : 'NO_WS';
    wsLog(`sendGeoreferencing llamado — estado WS: ${readyStateLabel} | leadDetail: ${!!leadDetail} | coordinates: ${!!coordinates} | email: ${!!email}`);

    if (!ws || !isWsReady) {
      wsWarn(`❌ Abortado: WebSocket no está OPEN (estado actual: ${readyStateLabel})`);
      handleError('WebSocket no está conectado o aún está reconectando', onError);
      return;
    }

    if (!leadDetail) {
      wsWarn('❌ Abortado: leadDetail es null o undefined');
      handleError('No se ha obtenido la información del lead', onError);
      return;
    }

    if (!email) {
      wsWarn('❌ Abortado: email no disponible');
      handleError('No se ha obtenido el email del agente', onError);
      return;
    }

    if (!coordinates) {
      wsWarn('❌ Abortado: no hay coordenadas seleccionadas');
      handleError('No hay coordenadas seleccionadas', onError);
      return;
    }

    // ciudad_id es obligatorio en el payload CO — sin él el backbone rechaza con 400
    if (country === 'CO' && !leadDetail.location?.city?.id) {
      wsWarn('❌ Abortado: el lead no tiene ciudad configurada (ciudad_id requerido para CO)');
      handleError('Nid sin información de ciudad. Contacta al equipo de Sellers', onError);
      return;
    }

    const payload = buildPayload(
      country,
      email,
      coordinates,
      Number(leadDetail.id),
      leadDetail,
      overrides
    );
    wsLog('📋 Campos extra incluidos en payload:', country === 'MX'
      ? {
          property_type_id: (payload as any).property_type_id,
          address: (payload as any).address,
        }
      : {
          ciudad_id: (payload as any).ciudad_id,
          tipo_inmueble_id: (payload as any).tipo_inmueble_id,
          direccion: (payload as any).direccion,
          conjunto_edificio: (payload as any).conjunto_edificio,
          num_habitaciones: (payload as any).num_habitaciones,
          num_ascensores: (payload as any).num_ascensores,
        }
    );
    const messageBody = JSON.stringify({
      action: selectWebSocketAction(country),
      Authorization: 'default',
      body: JSON.stringify(payload),
    });

    wsLog('📤 Enviando payload:', payload);

    setIsLoading(true);
    pendingRequestRef.current = true;
    toast.info('Actualizando información', {
      toastId: 'loadingGeoreferencing',
    });

    try {
      ws.send(messageBody);
      wsLog('✉️ Mensaje enviado correctamente, esperando respuesta del backbone...');
    } catch (sendError) {
      wsError('❌ ws.send() falló:', sendError);
      pendingRequestRef.current = false;
      setIsLoading(false);
      toast.dismiss('loadingGeoreferencing');
      handleError('No se pudo enviar la solicitud, intenta de nuevo', onError);
    }
  };

  const hasCityId = country !== 'CO' || !!leadDetail?.location?.city?.id;

  return {
    sendGeoreferencing,
    isLoading,
    canSend: !!(isWsReady && leadDetail && coordinates && email && hasCityId),
  };
};
